import { CounterOffer } from './../../pages/CounterOffer/CounterOffer';
import { URL_ENDPOINTS } from '../../constants';
import { ActivatedLoan } from '../../interfaces/ActivatedLoan';
import { CreditProposal } from '../../interfaces/CreditProposal';
import {
  CreditProposalResult,
  CreditProposalUpdate,
} from '../../interfaces/CreditProposalResult';
import { DecisionEngine } from '../../interfaces/DecisionEngine';
import { CustomerClassification } from '../../interfaces/CustomerClassification';
import { axiosInstance as Api } from '../../utils/api-helper';
import { StoredPaymentCards } from '../../interfaces/StoredPaymentCards';
import { ReusePaymentCardResponse } from '../../interfaces/ReusePaymentCardReponse';
import { ReusePaymentCardRequest } from '../../interfaces/ReusePaymentCardRequest';
import { NotificationType } from '../../interfaces/NotificationType';
import {
  BankreaderAttempts,
  BankreaderResponse,
} from '../../interfaces/Bankreaders';
import { RefinanceCreditProposal } from '../../interfaces/Refinance';

const CreditProposalService = {
  create: async (data: CreditProposal): Promise<CreditProposalResult> => {
    return Api.post(URL_ENDPOINTS.CREDIT_PROPOSAL, data);
  },
  update: async (
    data: CreditProposalUpdate[],
    creditProposalId: string
  ): Promise<CreditProposalResult> => {
    return Api.patch(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}`,
      data
    );
  },
  getCreditProposal: async (
    creditProposalId: string
  ): Promise<CreditProposalResult> => {
    return Api.get(URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}`);
  },
  getDecision: async (creditProposalId: string): Promise<DecisionEngine> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/decisions`
    );
  },

  activateCreditProposal: async (
    creditProposalId: string
  ): Promise<ActivatedLoan> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/loans`
    );
  },
  deleteCreditProposal: async (creditProposalId: string): Promise<any> => {
    return Api.delete(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}?reason=ACL`
    );
  },
  deleteCounterOffer: async (creditProposalId: string): Promise<any> => {
    return Api.delete(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}?reason=RBP`
    );
  },
  postClassification: async (
    creditProposalId: string,
    data: CreditProposal
  ): Promise<CustomerClassification> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL +
        `/${creditProposalId}/customer-classification`,
      data
    );
  },
  postReusePaymentCard: async (
    creditProposalId: string,
    payment_id: string,
    payment_method_id: string
  ): Promise<ReusePaymentCardResponse> => {
    const request: ReusePaymentCardRequest = {
      payment_id,
      payment_method_id,
    };
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/reuse-payment`,
      request
    );
  },
  getStoredPaymentCards: async (
    creditProposalId: string
  ): Promise<StoredPaymentCards> => {
    return Api.get(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/payment-cards`
    );
  },
  sendNotification: async (
    creditProposalId: string,
    notificationType: any
  ): Promise<void> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/notifications`,
      {
        notify_type: notificationType,
      }
    );
  },
  createBankreader: async (
    creditProposalId: string,
    notificationType: NotificationType
  ): Promise<BankreaderAttempts> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/bankreaders`,
      {
        notify_type: notificationType,
      }
    );
  },
  getBankreader: async (
    creditProposalId: string
  ): Promise<BankreaderResponse> => {
    return Api.get(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/bankreaders`
    );
  },
  updateBankreaderOk: async (
    creditProposalId: string,
    bankreaderId: string
  ): Promise<BankreaderAttempts> => {
    return Api.patch(
      URL_ENDPOINTS.CREDIT_PROPOSAL +
        `/${creditProposalId}/bankreaders/${bankreaderId}`,
      {
        status: 'OK',
      }
    );
  },
  verifyBankreader: async (creditProposalId: string): Promise<any> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/verify`
    );
  },
  refinances: async (
    creditProposalId: string,
    data: RefinanceCreditProposal
  ): Promise<CreditProposalResult> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/refinances`,
      data
    );
  },
  threatMetrix: (creditProposalId: string, sessionId: string) => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL +
        `/${creditProposalId}/enrichment-threatmetrix`,
      {
        session_id: sessionId,
        async: false,
      }
    );
  },
  AssignCounterOffer: async (
    creditProposalId: string,
    CounterOfferId: string
  ): Promise<CreditProposalResult> => {
    return Api.post(
      URL_ENDPOINTS.CREDIT_PROPOSAL + `/${creditProposalId}/assign-counter-offer`,
      {
        counter_offer_id: CounterOfferId,
      }
    );
  },
};

export default CreditProposalService;
